import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { sameValuesExceptLast } from 'helpers/arrays';
import { last } from 'lodash';
import { PaginatedData } from 'types/pagination';

const usePaginatedCount = (queryKey: unknown[]): [number, Dispatch<SetStateAction<number>>] => {
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();
    const [count, setCount] = useState(0);

    useEffect(() => {
        const unsubscribe = queryClient.getQueryCache().subscribe(e => {
            const arr1 = e.query.queryKey;
            const arr2 = queryKey;

            const isSameValuesExceptLast = sameValuesExceptLast(arr1, arr2);
            const isLastElementSameType = typeof last(arr1) === typeof last(arr2);
            const isMatch = isSameValuesExceptLast && isLastElementSameType;

            if (isMatch && e.query.isActive()) {
                setCount(prevCount => {
                    const data = e.query.state.data;
                    if (data) {
                        const total = (data as PaginatedData<unknown>).meta.total;
                        return prevCount !== total ? total : prevCount;
                    }

                    return prevCount;
                });
            }
        });

        return () => unsubscribe();
    }, [accountId, queryClient, queryKey]);

    return [count, setCount];
};

export { usePaginatedCount };
